import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../../../hooks/use-document-title';
import { usePortalLiveRefresh } from '../../../hooks/use-portal-live-refresh';
import {
  ListSkeleton,
  LoadError,
} from '../../dashboard/dashboard-shared-components/load-state';
import { Collapse, listItemMotion, Staleable, StateSwitch } from '../../../../motion';
import {
  TenantVisitor,
  getTenantVisitorsPage,
} from '../../../../api/tenant-client';
import { createPass, listPasses } from '../../../../api/tenant-passes-client';
import {
  PassHome,
  PassSpecInput,
  ResidentPass,
  ResidentPassHome,
} from '../../../../interfaces/visitor-pass';
import { parseQuickAdd } from '../../../../utils/pass-quick-add';
import { PASSES_CHANGED_EVENT } from '../portal-iq';
import { StatusPill } from '../../../status-pill/status-pill';
import { AppButton } from '../../../button/app-button';
import { InfoBanner } from '../../../onboarding/onboarding.styled';
import { PassEditorModal } from '../../../pass-editor/pass-editor';
import { QuickAddField } from '../../../pass-editor/quick-add-field';
import { ActionWithReason } from '../../dashboard/dashboard-shared-components/disabled-reason';
import {
  LIVE_VISITOR_EVENTS,
  ListMore,
  PAGE_SIZE,
  primaryHome,
  readFailure,
  rowZone,
  usePagedPanel,
  usePanel,
  usePortalOverview,
  whenAt,
  zoneNote,
  zonesOf,
  zoneTag,
} from '../portal-common';
import {
  Card,
  EmptyState,
  ListRow,
  PageNote,
  RowMain,
  RowMeta,
  RowPill,
  RowTitle,
  Section,
  SectionHead,
  SectionHint,
  SectionTitle,
  TabLead,
  TabTitle,
} from '../portal-kit.styled';
import { PassCard, buildingToday } from './pass-card';
import { AddBar, AddLead, EndedToggle, InlinePills } from './portal-visitors.styled';
import { PassSuggestions } from './suggestion-card';

/**
 * VISITORS — who is expected for your home, and who the desk has signed in.
 *
 * Two sections (RP-D11), with — Plan 5 — "Lobbero remembers" above them:
 *   Suggestions    "Ines has visited on 3 Thursdays. Make this a regular
 *                  visit?" (suggestion-card.tsx), silent when there is none.
 *   Expected       the household's visitor passes (resident-portal Plan 3),
 *                  each one a row that opens its own page, and — Plan 4 —
 *                  "Add a visitor", the shared pass editor in resident mode.
 *   Recent visits  the Plan 1 list, now marking a visit that came on a pass.
 */

/**
 * YOUR OWN HOUSEHOLD IS NOT A VISITOR (bug D-1).
 *
 * The desk's "Resident In" records a resident coming home as a visitor-log row
 * of kind Resident Entry against their unit — so the portal listed "Ana ·
 * Resident Entry" under Your visitors, and the headline told the household that
 * somebody was signed in to see them. Migration 114 filters it at the source,
 * with the same trimmed, lower-cased comparison the bell uses; this is the same
 * rule again at the last step, so a server that ever sends one back cannot put
 * it on screen.
 */
export const isResidentEntry = (visitor: Pick<TenantVisitor, 'visitor_type'>): boolean =>
  (visitor.visitor_type || '').trim().toLowerCase() === 'resident entry';

const whoCame = (visitor: TenantVisitor): string => {
  const lead = visitor.first_name || 'A visitor';
  const others = (visitor.party_size || 1) - 1;
  if (others < 1) return lead;

  return `${lead} and ${others} other${others > 1 ? 's' : ''}`;
};

/* Said when nothing is expected yet and passes are on: what the feature is
   for, in one sentence, above the Add button. */
export const EXPECTED_EMPTY =
  'Add the people who visit regularly, like a cleaner or a carer, and the front desk will expect them.';
export const PASSES_OFF_TEXT = 'Your building has switched visitor passes off.';
export const ADD_VISITOR_LABEL = 'Add a visitor';
/* Nothing expected and nothing to add to (no home takes a pass): the section
   says so, where it used to show a heading over nothing (RP-9.9). */
export const NOBODY_EXPECTED = 'Nobody is expected.';
/* Passes at two buildings in two zones: one "Times in …" line would be wrong
   for half of them. */
export const MIXED_ZONES_NOTE = "Each pass is shown in its own building's time.";

/** The visible reason under a disabled "Add a visitor" (the server's limit). */
export const passLimitReason = (max: number): string =>
  `Your home already has ${max} visitor passes. End one to add another.`;

/* A pass counts against its unit's limit until it has ended. */
const liveOn = (passes: ResidentPass[], home: ResidentPassHome): number =>
  passes.filter(pass => pass.state !== 'ended' && pass.unit_id === home.unit_id).length;

const toEditorHome = (home: ResidentPassHome): PassHome => ({
  occupancy_id: home.occupancy_id,
  unit_id: home.unit_id,
  unit_label: home.unit_label,
  building_name: home.building_name,
  building_timezone: home.building_timezone,
});

/* ────────────────────────────  Expected  ──────────────────────────── */

const ExpectedSection: React.FC = () => {
  const panel = usePanel(listPasses);
  /* A pass added, changed or ended by the building, or a visitor signed in on
     one — the bell hears it, this section re-reads in place. */
  usePortalLiveRefresh(LIVE_VISITOR_EVENTS, panel.reload);
  /* …or changed from the Lobbero IQ panel, which sits over this screen. */
  const reloadPasses = panel.reload;
  useEffect(() => {
    window.addEventListener(PASSES_CHANGED_EVENT, reloadPasses);

    return () => window.removeEventListener(PASSES_CHANGED_EVENT, reloadPasses);
  }, [reloadPasses]);
  const [showEnded, setShowEnded] = useState(false);
  const [adding, setAdding] = useState(false);
  // Changes on every OPEN, never on close: each open is a fresh editor, and a
  // closing one keeps its key so it can animate out.
  const [editorKey, setEditorKey] = useState(0);

  const homes = panel.data?.homes || [];
  const passes = panel.data?.passes || [];
  const limits = panel.data?.limits;
  const current = passes.filter(pass => pass.state !== 'ended');
  const ended = passes.filter(pass => pass.state === 'ended');
  const allOff = homes.length > 0 && homes.every(home => !home.passes_enabled);
  const zone = homes[0]?.building_timezone;
  // The zones the passes on screen are in — theirs, not the first home's.
  const passZones = zonesOf(current, zone);
  const zoneLine =
    passZones.length > 1 ? MIXED_ZONES_NOTE : zoneNote(passZones[0] || zone);

  /* WHO MAY ADD, AND FOR WHICH HOME. A switched-off building takes no new
     pass (the banner says so instead of a button the server would refuse);
     a home at the server's limit is not offered; with every home at it the
     button stays, disabled, with the reason under it. */
  const max =
    limits && Number.isFinite(limits.max_live_per_unit) ? limits.max_live_per_unit : null;
  const enabledHomes = homes.filter(home => home.passes_enabled);
  const openHomes =
    max === null ? enabledHomes : enabledHomes.filter(home => liveOn(passes, home) < max);
  const canAdd = enabledHomes.length > 0;
  const atLimit = canAdd && openHomes.length === 0;
  /* What the editor OPENS with: the first home it offers. Once it is open,
     the home picked in it decides the date, the zone and what quick-add's
     "tomorrow" means (pass-editor.tsx hands those to the quick-add slot). */
  const addZone = openHomes[0]?.building_timezone || zone || 'UTC';
  const addToday = buildingToday(addZone);

  /* What the next editor opens with — only ever a sentence handed over by
     Lobbero IQ (below). The Add button opens an empty form. */
  const [prefill, setPrefill] = useState<Partial<PassSpecInput> | undefined>(undefined);

  const openEditor = () => {
    setPrefill(undefined);
    setEditorKey(n => n + 1);
    setAdding(true);
  };

  /* A SENTENCE FROM LOBBERO IQ (portal-iq.tsx). "My cleaner Maria comes every
     Tuesday 9 to 1", typed into the IQ panel, arrives here as router state and
     is read by the same local parser quick-add uses; the form opens filled in
     and waits — nothing is saved until the reader presses Save. Read ONCE: the
     state is cleared before anything else, so a reload or Back does not reopen
     it. A building that takes no passes, or a home at its limit, already says
     so on this screen, and then no form opens. */
  const location = useLocation();
  const navigate = useNavigate();
  const iqSentence = (location.state as { iqPass?: unknown } | null)?.iqPass;
  const ready = panel.status === 'ready';
  const canOpen = canAdd && !!limits && openHomes.length > 0;
  useEffect(() => {
    if (typeof iqSentence !== 'string' || !iqSentence || !ready) return;
    navigate(location.pathname, { replace: true, state: null });
    if (!canOpen) return;
    let fields: Partial<PassSpecInput> = {};
    try {
      fields = parseQuickAdd(iqSentence, addToday, addZone).fields || {};
    } catch {
      fields = {};
    }
    setPrefill(Object.keys(fields).length ? fields : undefined);
    setEditorKey(n => n + 1);
    setAdding(true);
    // eslint-disable-next-line
  }, [iqSentence, ready]);

  return (
    <>
      {panel.status === 'ready' && canAdd ? (
        <PassSuggestions homes={enabledHomes} limits={limits} onPassSaved={panel.reload} />
      ) : null}
      <Section aria-labelledby="portal-expected-title">
        <SectionHead>
          <SectionTitle id="portal-expected-title">Expected</SectionTitle>
          <SectionHint>{current.length ? `${current.length} ${current.length === 1 ? 'visitor' : 'visitors'}` : ''}</SectionHint>
        </SectionHead>

        <StateSwitch state={panel.status} minHeight={160}>
          {panel.status === 'loading' ? (
            <ListSkeleton rows={2} label="your expected visitors" />
          ) : panel.status === 'error' ? (
            <LoadError
              message={readFailure(panel.errorStatus, 'We could not load your expected visitors just now.')}
              onRetry={panel.reload}
            />
          ) : (
            <Staleable stale={panel.refreshing}>
              {panel.failed ? (
                <LoadError
                  message={readFailure(
                    panel.errorStatus,
                    'We could not refresh your expected visitors just now. What is shown may be out of date.'
                  )}
                  onRetry={panel.reload}
                />
              ) : null}
              {allOff ? <InfoBanner role="status">{PASSES_OFF_TEXT}</InfoBanner> : null}
              {canAdd ? (
                <AddBar>
                  {current.length ? null : <AddLead>{EXPECTED_EMPTY}</AddLead>}
                  <ActionWithReason reason={atLimit ? passLimitReason(max ?? 0) : null}>
                    {describedBy => (
                      <AppButton
                        disabled={atLimit}
                        aria-describedby={describedBy}
                        onClick={openEditor}
                      >
                        {ADD_VISITOR_LABEL}
                      </AppButton>
                    )}
                  </ActionWithReason>
                </AddBar>
              ) : null}
              {current.length ? (
                <Card>
                  {current.map((pass, index) => (
                    <PassCard
                      key={pass.pass_id}
                      pass={pass}
                      index={index}
                      today={buildingToday(pass.building_timezone)}
                    />
                  ))}
                </Card>
              ) : !canAdd && !allOff ? (
                <Card>
                  <EmptyState>{NOBODY_EXPECTED}</EmptyState>
                </Card>
              ) : null}
              {ended.length ? (
                <EndedToggle>
                  <AppButton
                    variant="ghost"
                    onClick={() => setShowEnded(open => !open)}
                    aria-expanded={showEnded}
                    aria-controls="portal-ended-passes"
                  >
                    {showEnded ? `Hide ended (${ended.length})` : `Ended (${ended.length})`}
                  </AppButton>
                  <Collapse open={showEnded} id="portal-ended-passes">
                    <Card>
                      {ended.map((pass, index) => (
                        <PassCard
                          key={pass.pass_id}
                          pass={pass}
                          index={index}
                          today={buildingToday(pass.building_timezone)}
                        />
                      ))}
                    </Card>
                  </Collapse>
                </EndedToggle>
              ) : null}
              {current.length && zoneLine ? (
                <PageNote data-clock-note="" data-clock-whose="building">
                  {zoneLine}
                </PageNote>
              ) : null}
            </Staleable>
          )}
        </StateSwitch>

        {canAdd && limits && openHomes.length ? (
          <PassEditorModal<ResidentPass>
            key={editorKey}
            open={adding}
            onClose={() => setAdding(false)}
            mode="resident"
            homes={openHomes.map(toEditorHome)}
            limits={limits}
            today={addToday}
            zone={addZone}
            prefill={prefill}
            quickAdd={api => <QuickAddField api={api} today={api.today} zone={api.zone} />}
            onSubmit={(spec, extra) =>
              createPass(
                extra.host_occupancy_id ? { ...spec, host_occupancy_id: extra.host_occupancy_id } : spec
              )
            }
            onSaved={() => {
              setAdding(false);
              panel.reload();
            }}
          />
        ) : null}
      </Section>
    </>
  );
};

/* ─────────────────────────  Recent visits  ────────────────────────── */

export const PortalVisitors: React.FC = () => {
  useDocumentTitle('Your visitors');
  const { data: overview } = usePortalOverview();
  const tz = primaryHome(overview)?.building_timezone;
  const list = usePagedPanel(getTenantVisitorsPage);
  /* The desk signs a guest in → the bell rings → this list re-reads, in place. */
  usePortalLiveRefresh(LIVE_VISITOR_EVENTS, list.reload);

  const visits = list.items.filter(visitor => !isResidentEntry(visitor));
  /* EACH VISIT IN ITS OWN BUILDING'S CLOCK (RP-9.9) — see portal-parcels. */
  const zones = zonesOf(visits, tz);
  const mixed = zones.length > 1;
  /* A row the guard dropped was still counted in the server's total. */
  const dropped = list.items.length - visits.length;
  const inside = visits.filter(visitor => visitor.still_inside).length;

  return (
    <>
      <TabTitle>Your visitors</TabTitle>
      <TabLead>Who is expected at your home, and who the front desk has signed in.</TabLead>

      <ExpectedSection />

      <Section>
        <SectionHead>
          <SectionTitle>Recent visits</SectionTitle>
          <SectionHint>{inside ? `${inside} inside now` : ''}</SectionHint>
        </SectionHead>

        <StateSwitch state={list.status} minHeight={360}>
          {list.status === 'loading' ? (
            <ListSkeleton rows={4} label="your visitors" />
          ) : list.status === 'error' ? (
            <LoadError
              message={readFailure(list.errorStatus, 'We could not load your visitors just now.')}
              onRetry={list.reload}
            />
          ) : (
            <Staleable stale={list.refreshing}>
              {list.failed ? (
                <LoadError
                  message={readFailure(
                    list.errorStatus,
                    'We could not refresh your visitors just now. What is shown may be out of date.'
                  )}
                  onRetry={list.reload}
                />
              ) : null}
              <Card>
                {visits.length === 0 ? (
                  <EmptyState>Nobody has been logged in for your unit yet.</EmptyState>
                ) : (
                  visits.map((visitor, index) => {
                    const at = rowZone(visitor, tz);

                    return (
                    <ListRow
                      key={visitor.id}
                      /* The cascade is per PAGE: a page appended by "Show
                         more" arrives from 0 rather than waiting out delays for
                         rows already on screen. No layout animation — the list
                         only grows. */
                      {...listItemMotion(index % PAGE_SIZE)}
                      layout={false}
                      data-at={visitor.arrived_at}
                      data-building-zone={at || undefined}
                    >
                      <RowMain>
                        <RowTitle>{whoCame(visitor)}</RowTitle>
                        {/* The whole line is the row's time text for the audits
                            (it starts with the arrival). One text node, on
                            purpose: the line is read as one sentence. */}
                        <RowMeta data-time="">
                          {whenAt(visitor.arrived_at, at)}
                          {visitor.visitor_type ? ` · ${visitor.visitor_type}` : ''}
                          {visitor.departed_at
                            ? ` · left ${whenAt(visitor.departed_at, at)}`
                            : ''}
                          {zoneTag(at, mixed)}
                        </RowMeta>
                        {visitor.on_pass ? (
                          <InlinePills>
                            <StatusPill tone="brand">On your pass</StatusPill>
                          </InlinePills>
                        ) : null}
                      </RowMain>
                      <RowPill tone={visitor.still_inside ? 'warning' : 'success'}>
                        {visitor.still_inside ? 'Inside' : 'Left'}
                      </RowPill>
                    </ListRow>
                    );
                  })
                )}
              </Card>
              <ListMore
                shown={visits.length}
                total={list.total - dropped}
                nextCursor={list.nextCursor}
                pages={list.pages}
                loadingMore={list.loadingMore}
                moreFailed={list.moreFailed}
                onMore={list.loadMore}
                noun="visit"
                what="visits"
                zone={mixed ? '' : zoneNote(zones[0] || tz)}
              />
            </Staleable>
          )}
        </StateSwitch>
      </Section>
    </>
  );
};
